import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { auditApi, type AuditEvent } from "../api/audit";
import { appsApi, type App } from "../api/apps";

export function AuditPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "sent" | "failed">("");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    appsApi.list().then((r) => {
      setApps(r.apps);
      if (r.apps.length > 0 && !appId) setAppId(r.apps[0]!.appId);
    });
  }, []);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    auditApi
      .list({ appId, status: statusFilter || undefined, limit: 50 })
      .then((r) => {
        setEvents(r.events);
        setCursor(r.nextCursor);
      })
      .finally(() => setLoading(false));
  }, [appId, statusFilter]);

  async function loadMore() {
    if (!appId || !cursor) return;
    const r = await auditApi.list({
      appId,
      status: statusFilter || undefined,
      cursor,
      limit: 50,
    });
    setEvents((e) => [...e, ...r.events]);
    setCursor(r.nextCursor);
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Audit log
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="App"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              {apps.map((a) => (
                <MenuItem key={a.appId} value={a.appId}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "" | "sent" | "failed")}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : events.length === 0 ? (
            <Typography color="text.secondary">No events yet for this app.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent at</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Latency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={e.messageId}>
                    <TableCell>{new Date(e.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{e.to.join(", ")}</TableCell>
                    <TableCell>{e.from}</TableCell>
                    <TableCell>{e.subject}</TableCell>
                    <TableCell>{e.provider}</TableCell>
                    <TableCell>
                      {e.status === "sent" ? (
                        <Chip label="sent" size="small" color="success" />
                      ) : (
                        <Chip label="failed" size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell>{e.latencyMs}ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {cursor && (
            <Box mt={2}>
              <Button onClick={loadMore}>Load more</Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
